import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

import InfoSection from "components/common/InfoSection";
import PageLoader from "components/common/PageLoader";

import { getDependentsDetails } from "services/Dependents";

import "./DependentsTab.scss";

function DependentsInfo({ dependentsData }) {
  if (!dependentsData || dependentsData.length === 0) {
    return <div className="w-100p mt-1">-</div>;
  }

  const groupedDependents = dependentsData.reduce((acc, dependent) => {
    const { relationship } = dependent;
    if (!acc[relationship]) {
      acc[relationship] = [];
    }
    acc[relationship].push(dependent);
    return acc;
  }, {});

  const parents = groupedDependents?.parent || [];
  const father = parents.find((p) => p.gender === "Male");
  const mother = parents.find((p) => p.gender === "Female");

  const parentsInfo = [];

  if (father) {
    parentsInfo.push(
      { label: "Father name", value: father.name || "N/A" },
      { label: "Father National ID", value: father.nationalId || "N/A" }
    );
  }

  if (mother) {
    parentsInfo.push(
      { label: "Mother name", value: mother.name || "N/A" },
      { label: "Mother National ID", value: mother.nationalId || "N/A" }
    );
  }

  return (
    <div className="flex flex-wrap">
      {parentsInfo.map((info) => (
        <div key={info.label} className="w-50p mt-1">
          <p className="text-headings-secondary heading-h5-regular">{info.label}</p>
          <p className="color-text-body-secondary para-body-m-medium">{info.value}</p>
        </div>
      ))}
    </div>
  );
}

function NoDataScreen() {
  return (
    <div className="dependents-nodata-wrapper">
      <p className="para-body-l-regular no-dependents-added-text">No dependents added yet</p>
    </div>
  );
}

function SpouseInfo({ dependentsData }) {
  if (!dependentsData || dependentsData.length === 0) {
    return <div className="w-100p mt-1">-</div>;
  }

  const groupedDependents = dependentsData.reduce((acc, dependent) => {
    const { relationship } = dependent;
    if (!acc[relationship]) {
      acc[relationship] = [];
    }
    acc[relationship].push(dependent);
    return acc;
  }, {});

  const spouses = groupedDependents?.spouse || [];
  const spouse = spouses[0];

  if (!spouse) {
    return <div className="w-100p mt-1">-</div>;
  }

  const spouseInfo = [
    { label: "Name", value: spouse.name || "N/A" },
    { label: "National ID", value: spouse.nationalId || "N/A" },
    {
      label: "Date of birth",
      value: spouse.dateOfBirth ? moment(spouse.dateOfBirth).format("MMM D, YYYY") : "N/A",
    },
  ];

  return (
    <div className="flex flex-wrap">
      {spouseInfo.map((info) => (
        <div key={info.label} className="w-50p mt-1">
          <p className="text-headings-secondary heading-h5-regular">{info.label}</p>
          <p className="color-text-body-secondary para-body-m-medium">{info.value}</p>
        </div>
      ))}
    </div>
  );
}

function ChildrenInfo({ dependentsData }) {
  if (!dependentsData || dependentsData.length === 0) {
    return <div className="w-100p mt-1">-</div>;
  }

  const groupedDependents = dependentsData.reduce((acc, dependent) => {
    const { relationship } = dependent;
    if (!acc[relationship]) {
      acc[relationship] = [];
    }
    acc[relationship].push(dependent);
    return acc;
  }, {});

  const children = groupedDependents?.child || [];

  if (children.length === 0) {
    return (
      <div className="w-100p mt-1">
        <p className="color-text-body-secondary para-body-m-medium">No children information available</p>
      </div>
    );
  }

  return (
    <div>
      {children.map((child, idx) => (
        <React.Fragment key={child.id || child.nationalId || child.name}>
          <div className="flex flex-wrap">
            <div className="w-50p mt-1">
              <p className="text-headings-secondary heading-h5-regular">Name</p>
              <p className="color-text-body-secondary para-body-m-medium">{child.name || "N/A"}</p>
            </div>
            <div className="w-50p mt-1">
              <p className="text-headings-secondary heading-h5-regular">National ID</p>
              <p className="color-text-body-secondary para-body-m-medium">{child.nationalId || "N/A"}</p>
            </div>
            <div className="w-50p mt-1">
              <p className="text-headings-secondary heading-h5-regular">Date of birth</p>
              <p className="color-text-body-secondary para-body-m-medium">
                {child.dateOfBirth ? moment(child.dateOfBirth).format("MMM D, YYYY") : "N/A"}
              </p>
            </div>
          </div>
          {idx < children.length - 1 && <div className="dependents-divider" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function EmployeeDependents() {
  const [dependentsData, setDependentsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const employeeDetails = useSelector((state) => state.GetEmployeeDetials.employeeDetails);
  const employeeID = employeeDetails?.employeeId;

  const fetchDependentsData = async () => {
    if (!employeeID) return;

    setIsLoading(true);
    try {
      const response = await getDependentsDetails(employeeID);
      setDependentsData(response?.data || []);
    } catch (error) {
      setDependentsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDependentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeID]);

  return (
    <div className="dependents-tab-container">
      {isLoading && <PageLoader />}

      {!isLoading && dependentsData?.length === 0 ? (
        <NoDataScreen />
      ) : (
        <div>
          <InfoSection header="Parents" position="top" noBottomBorder>
            <DependentsInfo dependentsData={dependentsData} />
          </InfoSection>
          <InfoSection header="Spouse" position="middle" noBottomBorder>
            <SpouseInfo dependentsData={dependentsData} />
          </InfoSection>
          <InfoSection header="Children" position="bottom">
            <ChildrenInfo dependentsData={dependentsData} />
          </InfoSection>
        </div>
      )}
    </div>
  );
}

export default EmployeeDependents;
